import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Lightbulb, Ruler, Palette, Clock, Wrench, MapPin, ArrowRight,
  ShieldCheck, ScrollText,
} from "lucide-react";

/**
 * Landing SEO — /enseigne-pharmacie-france
 * Texte fourni par le client (SEO optimisé). FR uniquement, pas de traduction.
 * Aucune affirmation non prouvée (voir CGV / Home pour la charte de langage).
 */

const CROIX_FEATURES = [
  "Éclairage LED vert fixe ou animé",
  "Affichage point par point",
  "Heure et température",
  "Programmation à distance",
  "Connexion Wi-Fi ou télécommande",
  "Capteur de luminosité",
  "Réglage automatique de l'intensité",
  "Programmation des horaires d'allumage",
  "Structure extérieure protégée selon la fiche technique du modèle",
];

const ENSEIGNES = [
  "Lettres boîtiers lumineuses en aluminium",
  "Lettres en inox brossé, poli ou finition dorée",
  "Lettres rétroéclairées avec halo lumineux",
  "Logos de pharmacie en relief",
  "Caducées pharmaceutiques lumineux",
  "Enseignes drapeaux recto verso",
  "Croix en néon LED sur support transparent",
  "Caissons lumineux et habillages de façade",
  "Signalétique de vitrine et marquage adhésif",
];

const PROJECT_INPUTS = [
  "Photographies de la façade",
  "Dimensions approximatives",
  "Adresse du projet",
  "Type de croix ou d'enseigne recherché",
  "Logo ou charte graphique",
  "Date d'installation souhaitée",
];

const Section = ({ eyebrow, title, subtitle, children, id }) => (
  <section
    id={id}
    className="py-16 lg:py-24 border-b border-white/5"
    data-testid={`section-${id}`}
  >
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
      {eyebrow && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#39FF14]" style={{ boxShadow: "0 0 12px #39FF14" }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
            {eyebrow}
          </span>
        </div>
      )}
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase leading-tight tracking-tight text-white">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base text-zinc-400 max-w-3xl font-body leading-relaxed">
          {subtitle}
        </p>
      )}
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

const BulletList = ({ items, testid }) => (
  <ul
    className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-zinc-300 font-body text-base leading-relaxed"
    data-testid={testid}
  >
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-2 w-1.5 h-1.5 bg-[#39FF14] rounded-full shrink-0" style={{ boxShadow: "0 0 8px #39FF14" }} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function EnseignePharmacieFrance() {
  useEffect(() => {
    // SEO tags — appliqués côté client (pas de SSR pour l'instant)
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = "Croix LED et enseignes de pharmacie en France | SignaPharma";
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute(
      "content",
      "Croix de pharmacie LED programmables, enseignes drapeaux, lettres boîtiers, caducées et logos lumineux. Étude et installation partout en France."
    );
    return () => {
      document.title = prevTitle || "Signapharma France";
      if (prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="page-enseigne-pharmacie-france">
      {/* HERO */}
      <section className="pt-28 lg:pt-32 pb-16 border-b border-white/5 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#39FF14 1px, transparent 1px), linear-gradient(90deg, #39FF14 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#39FF14]" style={{ boxShadow: "0 0 12px #39FF14" }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
              SignaPharma France · Auvergne-Rhône-Alpes
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight max-w-4xl">
            <span className="chrome-text">Croix LED et enseignes lumineuses</span>
            <span className="block mt-2 text-[#39FF14] neon-text-soft">pour pharmacies en France</span>
          </h1>
          <p className="mt-8 text-base lg:text-lg text-zinc-300 max-w-3xl font-body leading-relaxed">
            SignaPharma France conçoit, personnalise, fabrique et installe des enseignes lumineuses pour pharmacies et
            officines. Nous réalisons des <strong>croix de pharmacie LED</strong> fixes ou programmables, des{" "}
            <strong>enseignes drapeaux double face</strong>, des <strong>croix en néon LED</strong>, des{" "}
            <strong>lettres boîtiers</strong> en aluminium ou en inox, des <strong>caducées lumineux</strong> ainsi que
            des <strong>logos de pharmacie rétroéclairés</strong>.
          </p>
          <p className="mt-4 text-base text-zinc-400 max-w-3xl font-body leading-relaxed">
            Chaque projet est étudié sur mesure en fonction de la façade, de la distance de visibilité, des dimensions
            disponibles, de l'identité graphique de l'officine et des contraintes d'installation. Nos solutions
            s'adressent aux pharmacies en création, en rénovation, en transfert ou souhaitant remplacer une ancienne
            croix lumineuse.
          </p>
          <p className="mt-4 text-base text-zinc-400 max-w-3xl font-body leading-relaxed">
            Depuis notre atelier situé à <strong className="text-white">Saint-Jean-de-Gonville, dans l'Ain</strong>, nous
            intervenons prioritairement dans le <strong>Pays de Gex</strong>, la <strong>Haute-Savoie</strong>, la{" "}
            <strong>Savoie</strong>, le <strong>Rhône</strong>, l'<strong>Isère</strong> et la région{" "}
            <strong>Auvergne-Rhône-Alpes</strong>. Nous étudions également les projets situés à Paris, Lyon, Marseille,
            Toulouse, Nice, Bordeaux, Lille, Strasbourg, Nantes et partout en France métropolitaine, en fourniture
            seule, livraison ou installation selon la nature du chantier.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-neon" data-testid="cta-devis-hero">
              <span className="flex items-center gap-2">
                Demander un devis <ArrowRight size={16} />
              </span>
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 border border-white/20 text-white font-mono text-xs uppercase tracking-[0.2em] hover:border-[#39FF14] hover:text-[#39FF14] transition-colors"
              data-testid="cta-photos-hero"
            >
              Envoyer les photos de ma façade
            </Link>
            <Link
              to="/galerie"
              className="px-6 py-3 text-zinc-400 font-mono text-xs uppercase tracking-[0.2em] hover:text-[#39FF14] transition-colors"
              data-testid="cta-realisations-hero"
            >
              Découvrir nos réalisations →
            </Link>
          </div>
        </div>
      </section>

      {/* CROIX LED */}
      <Section
        id="croix-led"
        eyebrow="Croix de pharmacie"
        title="Croix de pharmacie LED fixes, animées et programmables"
        subtitle="Nos croix de pharmacie sont disponibles en différentes dimensions et configurations : simple face, double face, fixation en drapeau, installation en façade ou pose sur une structure existante."
      >
        <div className="mb-6 text-zinc-400 font-body text-sm">
          Selon le modèle sélectionné, elles peuvent intégrer :
        </div>
        <BulletList items={CROIX_FEATURES} testid="list-croix-features" />
        <div className="mt-8 p-5 border border-white/10 bg-white/[0.02] flex items-start gap-4">
          <ShieldCheck size={20} className="text-[#39FF14] mt-0.5 shrink-0" />
          <p className="text-sm text-zinc-400 font-body leading-relaxed">
            Les caractéristiques telles que l'indice de protection <strong className="text-white">IP65</strong>, la
            puissance, le mode de programmation et les fonctions d'affichage sont indiquées séparément pour chaque
            modèle, uniquement lorsqu'elles sont confirmées par sa documentation technique.
          </p>
        </div>
      </Section>

      {/* ENSEIGNES */}
      <Section
        id="enseignes"
        eyebrow="Enseignes complètes"
        title="Enseignes lumineuses et lettres boîtiers pour officines"
        subtitle="SignaPharma France réalise également des enseignes complètes pour moderniser et valoriser les façades de pharmacies."
      >
        <BulletList items={ENSEIGNES} testid="list-enseignes" />
        <p className="mt-8 text-sm text-zinc-400 font-body leading-relaxed max-w-3xl">
          Les <strong className="text-white">dimensions</strong>, <strong className="text-white">couleurs</strong>,{" "}
          <strong className="text-white">finitions</strong> et <strong className="text-white">températures d'éclairage</strong>{" "}
          sont personnalisées selon la charte graphique de l'officine et les contraintes du bâtiment.
        </p>
      </Section>

      {/* RÉGLEMENTATION */}
      <Section
        id="reglementation"
        eyebrow="Cadre légal"
        title="Une enseigne de pharmacie adaptée à la réglementation locale"
      >
        <div className="grid lg:grid-cols-2 gap-8 text-zinc-300 font-body text-base leading-relaxed">
          <div className="space-y-4">
            <p>
              L'emplacement, les dimensions et l'éclairage d'une enseigne doivent respecter le{" "}
              <strong className="text-white">Code de l'environnement</strong> ainsi que le{" "}
              <strong className="text-white">règlement local de publicité</strong> éventuellement applicable dans la
              commune. Une autorisation préalable peut être nécessaire selon la localisation et le type de dispositif.
            </p>
            <p>
              Lorsque l'activité signalée a cessé, les enseignes lumineuses doivent en principe être{" "}
              <strong className="text-[#39FF14]">éteintes entre 1 h et 6 h du matin</strong>. Des conditions
              particulières s'appliquent lorsque l'activité commence ou se termine entre minuit et 7 h, et les règles
              locales peuvent apporter des prescriptions complémentaires.
            </p>
          </div>
          <div className="space-y-4">
            <div className="p-5 border border-[#39FF14]/30 bg-[#39FF14]/5">
              <div className="flex items-center gap-2 mb-2">
                <ScrollText size={16} className="text-[#39FF14]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#39FF14]">
                  Référence légale
                </span>
              </div>
              <p className="text-sm text-zinc-300">
                Cette obligation découle notamment de l'
                <strong className="text-white">article R.581-59 du Code de l'environnement</strong>, plutôt que du seul
                arrêté du 27 décembre 2018.
              </p>
            </div>
            <p className="text-sm text-zinc-400">
              Les possibilités d'animation et d'affichage des croix sont configurées selon les besoins de l'officine et
              les règles applicables. La <strong className="text-white">croix verte</strong> et le{" "}
              <strong className="text-white">caducée</strong> sont des emblèmes protégés dont l'usage est réservé aux
              pharmaciens et établissements pharmaceutiques autorisés.
            </p>
          </div>
        </div>
      </Section>

      {/* PROJET */}
      <Section
        id="projet"
        eyebrow="Création · Rénovation · Transfert"
        title="Création ou rénovation de votre façade de pharmacie"
        subtitle="Vous ouvrez une nouvelle officine, transférez votre pharmacie ou souhaitez moderniser une enseigne existante ? Transmettez-nous ces éléments et nous préparons une proposition adaptée à votre projet."
      >
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="label-tech text-[#39FF14] mb-4">À transmettre</div>
            <BulletList items={PROJECT_INPUTS} testid="list-project-inputs" />
          </div>
          <div>
            <div className="label-tech text-[#39FF14] mb-4">Ce que contient notre proposition</div>
            <ul className="space-y-3 text-zinc-300 font-body text-base leading-relaxed">
              <li className="flex items-start gap-3">
                <Lightbulb size={18} className="text-[#39FF14] mt-1 shrink-0" />
                <span>Simulation visuelle du projet selon les photos transmises</span>
              </li>
              <li className="flex items-start gap-3">
                <Ruler size={18} className="text-[#39FF14] mt-1 shrink-0" />
                <span>Caractéristiques techniques et dimensions retenues</span>
              </li>
              <li className="flex items-start gap-3">
                <Palette size={18} className="text-[#39FF14] mt-1 shrink-0" />
                <span>Options disponibles (couleurs, finitions, animations)</span>
              </li>
              <li className="flex items-start gap-3">
                <Wrench size={18} className="text-[#39FF14] mt-1 shrink-0" />
                <span>Coût de la fourniture, de la livraison et de l'installation</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={18} className="text-[#39FF14] mt-1 shrink-0" />
                <span>Délais d'étude, de fabrication et de pose du projet</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#39FF14] mt-1 shrink-0" />
                <span>
                  Pose, nacelle, déplacement, raccordement électrique et SAV{" "}
                  <em className="text-zinc-500">précisés séparément dans chaque devis</em>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* CTA FINAL */}
      <section className="py-20 lg:py-28 relative overflow-hidden" data-testid="section-cta-final">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            background: "radial-gradient(circle at 50% 50%, #39FF14 0%, transparent 60%)",
          }}
        />
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center relative">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase leading-tight tracking-tight text-white">
            Vous recherchez une <span className="text-[#39FF14] neon-text-soft">croix de pharmacie LED</span> ou une
            enseigne lumineuse sur mesure ?
          </h2>
          <p className="mt-6 text-base lg:text-lg text-zinc-400 font-body max-w-2xl mx-auto">
            Envoyez les photographies de votre façade pour recevoir une première étude de votre projet. Nous vous
            répondons rapidement avec une proposition adaptée à vos contraintes et à votre budget.
          </p>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-4">
            <Link to="/contact" className="btn-neon" data-testid="cta-devis-final">
              <span className="flex items-center gap-2">
                Demander un devis <ArrowRight size={16} />
              </span>
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 border border-white/20 text-white font-mono text-xs uppercase tracking-[0.2em] hover:border-[#39FF14] hover:text-[#39FF14] transition-colors"
              data-testid="cta-photos-final"
            >
              Envoyer les photos de ma façade
            </Link>
            <Link
              to="/galerie"
              className="px-6 py-3 text-zinc-400 font-mono text-xs uppercase tracking-[0.2em] hover:text-[#39FF14] transition-colors"
              data-testid="cta-realisations-final"
            >
              Découvrir nos réalisations →
            </Link>
          </div>

          {/* Charte de langage — reformulation validée client */}
          <p className="mt-14 text-xs text-zinc-600 font-body max-w-2xl mx-auto italic leading-relaxed">
            Intervention et livraison partout en France selon la nature du projet. Pose, déplacement, moyens d'accès et
            SAV précisés dans le devis.
          </p>
        </div>
      </section>
    </div>
  );
}
